<script>
  // Authoring surface for the private cubes: one aggregate at a time, saved
  // straight into the Pod and counted over the records already there.
  //
  //   onsave    save and compute the cube.
  //   onpreview (aggregate) → { candidate }, computed over the private records.
  // The cubes there are, and removing one, are on Cubes (Catalog.svelte).
  //   datasets  the institution's datasets.json: a cube belongs to the one
  //             dataset its terms sit in, and may not span two.
  import Panel from './Panel.svelte';
  import Button from './Button.svelte';
  import Alert from './Alert.svelte';
  import {
    dimensionEligibility, eligibleDimensions, combineProblem,
    missingMarginals, suggestAggregateId, validateAggregateId,
  } from '../../../lib/disclosure_core.js';
  import { resolveDatasets, datasetOfCube } from '../../../lib/datasets.js';

  let {
    mapping, aggregateMapping = mapping, dataset = null, aggregations,
    onsave, onpreview, datasets = null,
    saveLabel = 'Add cube',
  } = $props();

  const eligible = $derived(eligibleDimensions(mapping));
  const ineligible = $derived(dimensionEligibility(mapping).filter((d) => !d.eligible));
  const marginals = $derived(missingMarginals(mapping, aggregations));
  // Every source's columns, with this source's as edited, decide which dataset
  // a cube's terms sit in.
  const placing = $derived({ records: { ...aggregateMapping?.records, ...mapping?.records },
    columns: [...(aggregateMapping?.columns ?? []), ...(mapping?.columns ?? [])] });
  const resolved = $derived(datasets ? resolveDatasets(datasets, placing) : []);
  const datasetName = (id) => resolved.find((d) => d.id === id)?.label ?? id;

  // The dataset's rules (step 7.6); a cube is counted under them.
  const threshold = $derived(dataset?.suppressBelow ?? 5);

  // ── The candidate ─────────────────────────────────────────────────────────
  let first = $state('');
  let second = $state('');
  let customId = $state('');

  const dimensions = $derived([first, second].filter(Boolean));
  const kind = $derived(dimensions.length === 2 ? 'combined' : 'single');
  const suggestedId = $derived(suggestAggregateId(dimensions));
  const aggregateId = $derived(customId.trim() || suggestedId);

  const shapeProblem = $derived.by(() => {
    if (!first) return 'Choose a dimension to break down by.';
    if (!second) return null;
    return combineProblem(eligible.find((d) => d.target === first), eligible.find((d) => d.target === second));
  });
  const idProblem = $derived(first ? validateAggregateId(aggregateId, aggregations) : null);
  const placed = $derived(first && resolved.length
    ? datasetOfCube({ dimensions }, aggregations, placing, resolved) : null);
  const datasetProblem = $derived(placed?.problem ? `This cube ${placed.problem}.` : null);
  const problem = $derived(shapeProblem ?? idProblem ?? datasetProblem);

  const candidate = $derived(problem ? null
    : { id: aggregateId, kind, dimensions, sharing: { mode: 'none' } });

  // A preview describes one exact proposal. Change the proposal and the numbers
  // on screen stop being about it, so Add is only offered while they still are.
  const signature = $derived(JSON.stringify(dimensions));
  let previewed = $state(null);
  let previewSignature = $state(null);
  const fresh = $derived(Boolean(previewed) && previewSignature === signature);

  let previewing = $state(false);
  let saving = $state(false);
  let error = $state(null);

  async function preview() {
    previewing = true; error = null;
    try {
      const result = await onpreview(candidate);
      previewed = result?.candidate ?? null;
      previewSignature = signature;
    } catch (e) {
      error = e.message; previewed = null;
    } finally {
      previewing = false;
    }
  }

  async function add() {
    if (!candidate || !fresh) return;
    saving = true; error = null;
    try {
      await onsave(candidate);
      first = ''; second = ''; customId = ''; previewed = null; previewSignature = null;
    } catch (e) {
      error = e.message;
    } finally {
      saving = false;
    }
  }

  // Fills the form from a suggested marginal and previews it in one gesture. The
  // offer is a shortcut into the same authoring path, never a second way to add.
  async function setUp(target) {
    first = target; second = ''; customId = ''; previewed = null;
    await preview();
  }

  const published = (summary) => {
    if (!summary) return null;
    const shown = summary.examples.map((e) => `${e.label} ${e.count}`).join(' · ');
    const more = summary.published - summary.examples.length;
    return more > 0 ? `${shown} · +${more} more` : shown || 'nothing';
  };

</script>

{#if marginals.length}
  <Panel>
    <h3 class="wf2-panel-title">Breakdowns you do not have yet</h3>
    <p class="wf2-text">
      Every labelled column can be counted on its own. These have no breakdown yet
      — set one up to see what it would publish. Nothing is added until you add it.
    </p>
    <div class="wf2-tablewrap" style="overflow-x:auto">
      <table class="wf2-table">
        <thead><tr><th>Break down by</th><th>From column</th><th>Treatment</th><th></th></tr></thead>
        <tbody>
          {#each marginals as d (d.target)}
            <tr>
              <td class="mono">{d.target}</td>
              <td>{d.source}</td>
              <td>{d.treatment}</td>
              <td><Button variant="ghost" onclick={() => setUp(d.target)}>Set up and preview</Button></td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </Panel>
{/if}

<Panel>
  <h3 class="wf2-panel-title">Add a cube</h3>
  {#if dataset}<p class="wf2-caption">Counting {dataset.label}: only its terms are offered, whichever of its sources fills them.</p>{/if}
  <p class="wf2-text">
    One dimension counts it on its own; adding a second crosses the two. See what
    it would publish before you add it — a cube can be safe and still say almost
    nothing once small groups are hidden. New cubes stay private until
    governance explicitly publishes them.
  </p>

  <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
    <label class="wf2-field" style="min-width:220px">Break down by
      <select class="wf2-control" bind:value={first}>
        <option value="">Choose…</option>
        {#each eligible as d}<option value={d.target}>{d.target}</option>{/each}
      </select>
    </label>
    <label class="wf2-field" style="min-width:220px">Crossed with (optional)
      <select class="wf2-control" bind:value={second}>
        <option value="">— nothing, count it on its own —</option>
        {#each eligible as d}<option value={d.target}>{d.target}</option>{/each}
      </select>
    </label>
    <label class="wf2-field" style="min-width:200px">Cube id
      <input class="wf2-control mono" bind:value={customId} placeholder={suggestedId || 'generated from the dimensions'} />
    </label>
  </div>
  <p class="wf2-caption">
    Saved as <span class="mono">kind: {kind}</span>{#if aggregateId} · <span class="mono">{aggregateId}</span>{/if}.
    {#if placed?.dataset}It belongs to <strong>{datasetName(placed.dataset)}</strong>, the dataset its terms sit in: a partner given that dataset gets this cube too.{/if}
  </p>

  <div class="wf2-row" style="gap:12px; margin-top:12px">
    <Button variant="secondary" onclick={preview} disabled={Boolean(problem) || previewing}>
      {previewing ? 'Counting…' : 'Preview'}
    </Button>
    <Button variant="primary" onclick={add} disabled={!fresh || saving}>
      {saving ? 'Computing…' : saveLabel}
    </Button>
  </div>
  {#if problem}
    <p class="wf2-hint">{problem}</p>
  {:else if !fresh}
    <p class="wf2-hint">Preview it first — the numbers are the decision.</p>
  {/if}

  {#if error}
    <Alert title="Cannot add cube" variant="danger"><p>{error}</p></Alert>
  {/if}

  {#if fresh && previewed && previewed.minPerColumn != null}
    <div class="wf2-tablewrap" style="overflow-x:auto; margin-top:16px">
      <table class="wf2-table">
        <thead><tr><th>Period</th><th>Records with a value</th><th>Shared</th></tr></thead>
        <tbody>
          {#each previewed.periods as p}
            <tr><td>{p.period ?? 'all'}</td><td>{p.records}</td><td>{p.withheld ? 'withheld' : 'whole'}</td></tr>
          {/each}
        </tbody>
      </table>
    </div>
    <Alert title={previewed.verdict} variant={previewed.variant}>
      <p class="wf2-text">
        One column on its own, so it is shared as a whole distribution, small counts
        included, wherever at least {previewed.minPerColumn} records make it up. What gets
        published: {published(previewed)}.
      </p>
    </Alert>
  {:else if fresh && previewed}
    <div class="wf2-tablewrap" style="overflow-x:auto; margin-top:16px">
      <table class="wf2-table">
        <thead><tr>
          <th>Possible groups</th><th>Hidden (under {threshold})</th><th>Published</th><th>What gets published</th>
        </tr></thead>
        <tbody><tr>
          <td>{previewed.possible}</td>
          <td>{previewed.hidden}</td>
          <td>{previewed.published} ({previewed.lossPct}% hidden)</td>
          <td>{published(previewed)}</td>
        </tr></tbody>
      </table>
    </div>
    <Alert title={previewed.verdict} variant={previewed.variant}>
      <p class="wf2-text">
        Everything published is a group of {threshold}+, so it is safe to share.
        {#if previewed.lossPct >= 50}
          But {previewed.lossPct}% of the groups are too small to appear, so what
          you publish here describes the larger groups only — anyone reading it
          would be reading about part of your records, not all of them. Two
          separate breakdowns, one per column, usually keep far more.
        {/if}
      </p>
    </Alert>
  {/if}

  {#if ineligible.length}
    <details class="drop-list" style="margin-top:16px">
      <summary>{ineligible.length} labelled {ineligible.length === 1 ? 'column is' : 'columns are'} not offered here</summary>
      <div style="padding: var(--space-2) var(--space-3)">
        <ul class="wf2-text">
          {#each ineligible as d}
            <li><span class="mono">{d.target || d.source}</span> — {d.reason}.</li>
          {/each}
        </ul>
        <p class="wf2-caption">
          Change any of these on its source's mapping; a column becomes available here as soon as its labelling allows it.
        </p>
      </div>
    </details>
  {/if}
</Panel>
